import React, { useMemo } from 'react';
import { SpaceProps } from 'styled-system';
import { Headline } from '../Typography/Typography';
import Box from '../Box/Box';
import Image from '../Image/Image';
import { gridInlinePadding } from '../Grid/Grid';
import { getValueAndUnit, rem } from 'polished';
import get from 'lodash/get';
import { isLightColor } from '../../utils/isLightColor';

interface StyledTabsGroupProps extends SpaceProps {
    activeSlide?: number;
    items: any;
    slider: any;
    colorScheme?: 'primary' | 'secondary';
    showIcon?: boolean;
    isCentered?: boolean;
    display: any;
    sectionBackground?: string | null;
}

const TabsGroup: React.FC<StyledTabsGroupProps> = ({
    activeSlide,
    items,
    slider,
    colorScheme,
    showIcon = true,
    isCentered,
    sectionBackground = 'neutral.white',
    ...props
}) => {
    const isLight = isLightColor(sectionBackground);
    const tabsColorScheme = colorScheme
        ? colorScheme
        : sectionBackground === 'neutral.white'
        ? 'primary'
        : sectionBackground.split('.').slice(0, -1).join('.');
    const scheme = {
        primary: {
            bg: 'primary.1300',
            color: isLight ? 'primary.1300' : 'neutral.white',
            colorActive: 'neutral.white',
        },
        secondary: {
            bg: 'tertiary.turquoise.1500',
            color: isLight ? 'tertiary.turquoise.1500' : 'neutral.white',
            colorActive: 'neutral.white',
        },
    };
    const currentColorScheme = get(scheme, tabsColorScheme);

    return (
        <Box
            overflowX="auto"
            px={{
                _: rem(getValueAndUnit(gridInlinePadding._)[0]),
                md: rem(getValueAndUnit(gridInlinePadding.md)[0]),
            }}
            pb={rem(12)}
            position="relative"
            marginInlineStart={{
                _: rem(-getValueAndUnit(gridInlinePadding._)[0]),
                md: rem(-getValueAndUnit(gridInlinePadding.md)[0]),
            }}
            width={{
                _: `calc(100% + ${rem(
                    getValueAndUnit(gridInlinePadding._)[0] * 2
                )})`,
                md: `calc(100% + ${rem(
                    getValueAndUnit(gridInlinePadding.md)[0] * 2
                )})`,
            }}
            display={{ _: 'flex', lg: 'none' }}
            flexWrap="nowrap"
            justifyContent={
                isCentered ? ['flex-start', 'center'] : 'flex-start'
            }
            gap={rem(8)}
            {...props}
        >
            {items.map(({ title, icon }, i) => (
                <Box
                    key={i}
                    minWidth="max-content"
                    onClick={() => {
                        slider?.current?.slickGoTo(i);
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        bg={activeSlide === i && currentColorScheme.bg}
                        borderRadius="100px"
                        padding="12px 18px"
                        border="2px solid"
                        borderColor={currentColorScheme.bg}
                        css={css`
                            gap: 8px;
                            cursor: pointer;
                        `}
                    >
                        {showIcon && <Image img={icon} />}
                        <Headline
                            color={
                                activeSlide === i
                                    ? currentColorScheme.colorActive
                                    : currentColorScheme.color
                            }
                        >
                            {title?.text}
                        </Headline>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default TabsGroup;
